import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import rentsService from "../services/rentsService";
import styles from "../assets/rentDatesEdit.module.css";
import Loader from "../components/Loader";

const RentDatesEdit = () => {
  const { rentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rentData, setRentData] = useState(null);
  const [formData, setFormData] = useState({
    date_start: "",
    date_end: "",
  });

  useEffect(() => {
    loadRentData();
  }, [rentId]);

  const loadRentData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await rentsService.getRentDatesEdit(rentId);
      console.log("🔍 [FRONTEND DEBUG] Rent dates edit data:", data);
      
      if (data.success && data.rent) {
        setRentData(data.rent);
        setFormData({
          date_start: data.rent.date_start
            ? data.rent.date_start.split("T")[0]
            : "",
          date_end: data.rent.date_end ? data.rent.date_end.split("T")[0] : "",
        });
      } else {
        throw new Error(data.message || "Failed to load rent data");
      }
    } catch (err) {
      console.error("Error loading rent data:", err);
      setError(err.response?.data?.message || err.message || "Failed to load rent data");
      setRentData(null);
      setFormData({
        date_start: "",
        date_end: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Convert ISO date (YYYY-MM-DD) to Laravel format (d/m/Y)
  const convertToDMY = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };

  // Convert Laravel format (d/m/Y) to ISO date (YYYY-MM-DD)
  const convertToISO = (dmyDate) => {
    if (!dmyDate) return "";
    const [day, month, year] = dmyDate.split("/");
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date_start) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Only validate end date if provided
    if (formData.date_end && new Date(formData.date_start) >= new Date(formData.date_end)) {
      toast.error("End date must be after start date");
      return;
    }

    try {
      setSaving(true);
      
      // Laravel Parity: Convert ISO format to d/m/Y format before sending
      const submitData = {
        date_start: convertToDMY(formData.date_start),
        date_end: convertToDMY(formData.date_end),
      };
      
      console.log("📤 [FRONTEND] Sending dates in Laravel format:", submitData);
      
      await rentsService.updateRentDates(rentId, submitData);
      toast.success("Rent dates updated successfully");
      navigate("/rents");
    } catch (err) {
      console.error("Error updating rent dates:", err);
      
      // Handle validation errors from backend
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        toast.error(errorMessages[0] || "Validation failed");
      } else {
        toast.error(err.response?.data?.message || "Failed to update rent dates");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/rents");
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className={styles["rent-dates-edit"]}>
        <div className={styles["error-container"]}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles["rent-dates-edit"]}>
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/dashboard">Dashboard</a>
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/rents">/ Rents</a>
        </span>

        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Edit Dates</span>
        </div>
      </div>

      <div className={styles["main-content"]}>
        <div className={styles["panel"]}>
          <div className={styles["panel-heading"]}>
            <h3 className={styles["panel-title"]}>Edit Rent Dates</h3>
          </div>
          <div className={styles["panel-body"]}>
            <div className={styles["guardian-overview"]}>
              <h4>Guardian Overview</h4>
              <div className={styles["overview-grid"]}>
                <div className={styles["overview-item"]}>
                  <label>UGD:</label>
                  <span>{rentData?.unique_guardian_number || "-"}</span>
                </div>
                <div className={styles["overview-item"]}>
                  <label>Guardian:</label>
                  <span>{rentData?.guardianName || "-"}</span>
                </div>
                <div className={styles["overview-item"]}>
                  <label>Room:</label>
                  <span>{rentData?.roomName || "-"}</span>
                </div>
                <div className={styles["overview-item"]}>
                  <label>Property:</label>
                  <span>{rentData?.propertyName || "-"}</span>
                </div>
              </div>
            </div>

            <div className={styles["edit-form"]}>
              <h4>Edit Dates</h4>
              <form onSubmit={handleSubmit}>
                <div className={styles["form-group"]}>
                  <label htmlFor="date_start">Start Date *</label>
                  <input
                    type="date"
                    id="date_start"
                    name="date_start"
                    className={styles["form-control"]}
                    value={formData.date_start}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="date_end">End Date</label>
                  <input
                    type="date"
                    id="date_end"
                    name="date_end"
                    className={styles["form-control"]}
                    value={formData.date_end}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles["form-actions"]}>
                  <button
                    style={{
                      background: "#1B2046",
                      outline: "none",
                      border: "none",
                    }}
                    type="submit"
                    className={styles["btn-primary"]}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentDatesEdit;
